import React, { useEffect, useState } from 'react';
import {
  FileText, Loader2, RefreshCw, Trash2, Download, Copy, CheckCircle, Clock, Filter, Pencil,
  Send, ThumbsUp, ThumbsDown, Check, Archive, BarChart3, Instagram, X, Calendar,
  Image as ImageIcon,
} from 'lucide-react';
import { supabase } from '../lib/supabase';
import { adminFetch } from '../lib/api';
import { useNavigate } from 'react-router-dom';

const STATUS_CONFIG: Record<string, { label: string; cls: string; color: string }> = {
  draft: { label: 'Entwurf', cls: 'badge-neutral', color: 'var(--text-muted)' },
  ready_for_review: { label: 'Zur Prüfung', cls: 'badge-warning', color: '#F59E0B' },
  approved: { label: 'Freigegeben', cls: 'badge-info', color: '#3B82F6' },
  scheduled: { label: 'Geplant', cls: 'badge-info', color: '#8B5CF6' },
  posted: { label: 'Gepostet', cls: 'badge-success', color: '#10B981' },
  failed: { label: 'Fehlgeschlagen', cls: 'badge-danger', color: '#EF4444' },
  archived: { label: 'Archiviert', cls: 'badge-neutral', color: '#6B7280' },
};

const CATEGORY_LABELS: Record<string, { label: string; emoji: string }> = {
  urgency: { label: 'Urgency / FOMO', emoji: '🔥' },
  social_proof: { label: 'Social Proof', emoji: '⭐' },
  pain_point: { label: 'Pain Point', emoji: '😰' },
  action: { label: 'Action', emoji: '🎯' },
  transformation: { label: 'Transformation', emoji: '🦋' },
  tip: { label: 'Tipp', emoji: '💡' },
  quiz: { label: 'Prüfungsfrage', emoji: '❓' },
  fun_fact: { label: 'Fun Fact', emoji: '🤯' },
  seasonal: { label: 'Saisonal', emoji: '🌸' },
  motivation: { label: 'Motivation', emoji: '💪' },
  custom: { label: 'Eigener Prompt', emoji: '✏️' },
};

const REJECTION_REASONS: { value: string; label: string }[] = [
  { value: 'brand_mismatch', label: 'Nicht markenkonform' },
  { value: 'weak_hook', label: 'Schwacher Hook' },
  { value: 'bad_cta', label: 'Schwacher CTA' },
  { value: 'content_error', label: 'Inhaltlicher Fehler' },
  { value: 'wrong_timing', label: 'Falsches Timing' },
  { value: 'other', label: 'Sonstiges' },
];

export const MarketingPostsPage: React.FC = () => {
  const [posts, setPosts] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [categoryFilter, setCategoryFilter] = useState<string>('all');
  const [copiedId, setCopiedId] = useState<string>('');
  const [deleteConfirmId, setDeleteConfirmId] = useState<string>('');
  const [actionLoading, setActionLoading] = useState<string>(''); // postId being actioned
  const [rejectModalPost, setRejectModalPost] = useState<any>(null);
  const [rejectReason, setRejectReason] = useState('');
  const [rejectComment, setRejectComment] = useState('');
  const [scheduleModalPost, setScheduleModalPost] = useState<any>(null);
  const [scheduleDate, setScheduleDate] = useState('');
  const [scheduleTime, setScheduleTime] = useState('18:00');
  const [engagementModalPost, setEngagementModalPost] = useState<any>(null);
  const [engagementData, setEngagementData] = useState({ likes: 0, comments: 0, saves: 0, shares: 0, reach: 0, impressions: 0 });
  const navigate = useNavigate();

  useEffect(() => { loadPosts(); }, []);

  const loadPosts = async () => {
    setLoading(true);
    try {
      const resp = await adminFetch(`/api/admin/marketing/posts`, {
              });
      const data = await resp.json();
      setPosts(Array.isArray(data) ? data : []);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const apiAction = async (postId: string, action: string, body?: any) => {
    setActionLoading(postId);
    try {
      const resp = await adminFetch(`/api/admin/marketing/posts/${postId}/${action}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: body ? JSON.stringify(body) : undefined,
      });
      const data = await resp.json();
      if (data.success) await loadPosts();
      return data;
    } catch (err) {
      console.error(err);
    } finally {
      setActionLoading('');
    }
  };

  const deletePost = async (id: string) => {
    await adminFetch(`/api/admin/marketing/posts/${id}`, {
      method: 'DELETE',
          });
    setDeleteConfirmId('');
    await loadPosts();
  };

  const copyCaption = (post: any) => {
    const text = `${post.caption}\n\n${(post.hashtags || []).join(' ')}`;
    navigator.clipboard.writeText(text);
    setCopiedId(post.id);
    setTimeout(() => setCopiedId(''), 2000);
  };

  const handleApprove = async (post: any) => {
    setScheduleModalPost(post);
  };

  const confirmApprove = async (withSchedule: boolean) => {
    if (!scheduleModalPost) return;
    const body: any = {};
    if (withSchedule && scheduleDate) {
      body.scheduledAt = `${scheduleDate}T${scheduleTime}:00`;
    }
    await apiAction(scheduleModalPost.id, 'approve', body);
    setScheduleModalPost(null);
    setScheduleDate('');
  };

  const handleReject = async () => {
    if (!rejectModalPost) return;
    await apiAction(rejectModalPost.id, 'reject', {
      rejectionReason: rejectReason || 'other',
      rejectionComment: rejectComment || undefined,
    });
    setRejectModalPost(null);
    setRejectReason('');
    setRejectComment('');
  };

  const handleSaveEngagement = async () => {
    if (!engagementModalPost) return;
    await adminFetch(`/api/admin/marketing/posts/${engagementModalPost.id}/engagement`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(engagementData),
    });
    setEngagementModalPost(null);
    setEngagementData({ likes: 0, comments: 0, saves: 0, shares: 0, reach: 0, impressions: 0 });
  };

  const filteredPosts = posts
    .filter(p => statusFilter === 'all' || p.status === statusFilter)
    .filter(p => categoryFilter === 'all' || p.category === categoryFilter);

  const usedCategories = [...new Set(posts.map(p => p.category || 'custom'))].sort();

  // Status counts
  const counts: Record<string, number> = {};
  posts.forEach(p => { counts[p.status] = (counts[p.status] || 0) + 1; });

  if (loading) {
    return (
      <div>
        <div className="page-header"><h1><FileText size={24} /> Posts & Kalender</h1></div>
        <div style={{ textAlign: 'center', padding: 60 }}><Loader2 size={24} style={{ animation: 'spin 1s linear infinite' }} /></div>
      </div>
    );
  }

  return (
    <div>
      <div className="page-header">
        <h1><FileText size={24} /> Posts & Kalender</h1>
        <div className="flex gap-2">
          <button className="btn btn-secondary" onClick={loadPosts}><RefreshCw size={14} /> Aktualisieren</button>
          <button className="btn btn-primary" onClick={() => navigate('/marketing/create')}>+ Neuer Post</button>
        </div>
      </div>

      {/* KPIs */}
      <div className="grid-cols-4 mb-4" style={{ gridTemplateColumns: 'repeat(5, 1fr)' }}>
        <div className="kpi-card"><span className="kpi-label">Posts gesamt</span><span className="kpi-value">{posts.length}</span></div>
        <div className="kpi-card"><span className="kpi-label">Entwürfe</span><span className="kpi-value" style={{ color: 'var(--text-muted)' }}>{counts.draft || 0}</span></div>
        <div className="kpi-card">
          <span className="kpi-label">Zur Prüfung</span>
          <span className="kpi-value" style={{ color: '#F59E0B' }}>{counts.ready_for_review || 0}</span>
        </div>
        <div className="kpi-card"><span className="kpi-label">Geplant</span><span className="kpi-value" style={{ color: '#8B5CF6' }}>{counts.scheduled || 0}</span></div>
        <div className="kpi-card"><span className="kpi-label">Gepostet</span><span className="kpi-value" style={{ color: 'var(--success)' }}>{counts.posted || 0}</span></div>
      </div>

      {/* Status filter */}
      <div className="flex gap-2 mb-2" style={{ flexWrap: 'wrap' }}>
        <span className="text-xs text-muted" style={{ lineHeight: '28px', marginRight: 4 }}>Status:</span>
        {[
          { key: 'all', label: `Alle (${posts.length})` },
          { key: 'draft', label: `Entwürfe (${counts.draft || 0})` },
          { key: 'ready_for_review', label: `Zur Prüfung (${counts.ready_for_review || 0})` },
          { key: 'approved', label: `Freigegeben (${counts.approved || 0})` },
          { key: 'scheduled', label: `Geplant (${counts.scheduled || 0})` },
          { key: 'posted', label: `Gepostet (${counts.posted || 0})` },
        ].map(f => (
          <button key={f.key} className={`btn btn-sm ${statusFilter === f.key ? 'btn-primary' : 'btn-secondary'}`} onClick={() => setStatusFilter(f.key)}>
            {f.label}
          </button>
        ))}
      </div>

      {/* Category filter */}
      {usedCategories.length > 1 && (
        <div className="flex gap-2 mb-4" style={{ flexWrap: 'wrap' }}>
          <span className="text-xs text-muted" style={{ lineHeight: '28px', marginRight: 4 }}><Filter size={12} style={{ verticalAlign: 'middle' }} /> Kategorie:</span>
          <button className={`btn btn-sm ${categoryFilter === 'all' ? 'btn-primary' : 'btn-secondary'}`} onClick={() => setCategoryFilter('all')}>Alle</button>
          {usedCategories.map(cat => {
            const catInfo = CATEGORY_LABELS[cat] || { label: cat, emoji: '📝' };
            const count = posts.filter(p => (p.category || 'custom') === cat).length;
            return (
              <button key={cat} className={`btn btn-sm ${categoryFilter === cat ? 'btn-primary' : 'btn-secondary'}`} onClick={() => setCategoryFilter(cat)}>
                {catInfo.emoji} {catInfo.label} ({count})
              </button>
            );
          })}
        </div>
      )}

      {/* Posts Grid */}
      {filteredPosts.length === 0 ? (
        <div className="card" style={{ padding: 60, textAlign: 'center', color: 'var(--text-muted)' }}>
          <FileText size={48} style={{ marginBottom: 12, opacity: 0.3 }} />
          <p>Keine Posts für diese Filter.</p>
          <button className="btn btn-primary mt-3" onClick={() => navigate('/marketing/create')}>Post erstellen</button>
        </div>
      ) : (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))', gap: 16 }}>
          {filteredPosts.map(post => {
            const catInfo = CATEGORY_LABELS[post.category || 'custom'] || { label: 'Post', emoji: '📝' };
            const statusConf = STATUS_CONFIG[post.status] || STATUS_CONFIG.draft;
            const isActioning = actionLoading === post.id;
            return (
              <div key={post.id} className="card" style={{ overflow: 'hidden' }}>
                {/* Image */}
                {post.image_url ? (
                  <img src={post.image_url} alt="" style={{ width: '100%', aspectRatio: '1/1', objectFit: 'cover' }} />
                ) : (
                  <div style={{ width: '100%', aspectRatio: '16/9', background: 'linear-gradient(135deg, #E0370515, #FF6B3515)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexDirection: 'column', gap: 8, cursor: 'pointer' }}
                    onClick={async () => {
                      if (actionLoading) return;
                      setActionLoading(post.id);
                      try {
                        const resp = await adminFetch(`/api/admin/marketing/posts/${post.id}/generate-image`, {
                          method: 'POST',                         });
                        if (resp.ok) { await loadPosts(); }
                        else { const d = await resp.json(); alert(d.error || 'Fehler'); }
                      } catch (e: any) { alert(e.message); }
                      finally { setActionLoading(''); }
                    }}>
                    {actionLoading === post.id ? (
                      <><Loader2 size={24} style={{ animation: 'spin 1s linear infinite', color: '#E03705' }} /><span style={{ fontSize: 11, color: '#E03705' }}>Bild wird generiert...</span></>
                    ) : (
                      <><ImageIcon size={28} style={{ color: '#E03705', opacity: 0.6 }} /><span style={{ fontSize: 11, color: '#E03705', fontWeight: 500 }}>🖼️ Bild generieren</span></>
                    )}
                  </div>
                )}

                <div style={{ padding: 14 }}>
                  {/* Category + Status badges */}
                  <div className="flex justify-between items-center mb-2">
                    <span style={{ fontSize: 11, background: 'rgba(255,255,255,0.06)', padding: '2px 8px', borderRadius: 12 }}>
                      {catInfo.emoji} {catInfo.label}
                    </span>
                    <span style={{
                      fontSize: 10, padding: '2px 8px', borderRadius: 12, fontWeight: 600,
                      background: `${statusConf.color}15`, color: statusConf.color,
                    }}>
                      {statusConf.label}
                    </span>
                  </div>

                  <div style={{ fontWeight: 700, fontSize: 14, marginBottom: 4 }}>{post.title || 'Ohne Titel'}</div>
                  <p className="text-xs text-muted" style={{ display: '-webkit-box', WebkitLineClamp: 3, WebkitBoxOrient: 'vertical' as any, overflow: 'hidden', lineHeight: 1.5 }}>
                    {post.caption}
                  </p>

                  {/* Rejection badge */}
                  {post.rejection_count > 0 && (
                    <div className="text-xs mt-1" style={{ color: '#EF4444' }}>
                      ⚠️ {post.rejection_count}× abgelehnt
                    </div>
                  )}

                  <div className="text-xs text-muted mt-2" style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                    <Clock size={10} /> {new Date(post.created_at).toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit', year: '2-digit', hour: '2-digit', minute: '2-digit' })}
                  </div>

                  {/* Lifecycle Actions */}
                  <div className="flex gap-1 mt-3" style={{ flexWrap: 'wrap' }}>
                    {/* Edit */}
                    <button className="btn btn-primary btn-sm" onClick={() => navigate(`/marketing/create?edit=${post.id}`)} title="Bearbeiten">
                      <Pencil size={12} />
                    </button>

                    {/* Submit for review (draft only) */}
                    {post.status === 'draft' && (
                      <button className="btn btn-sm" style={{ background: '#F59E0B', color: '#fff', border: 'none' }}
                        onClick={() => apiAction(post.id, 'submit')} disabled={isActioning} title="Zur Prüfung einreichen">
                        {isActioning ? <Loader2 size={12} className="spin" /> : <Send size={12} />}
                      </button>
                    )}

                    {/* Approve/Reject (ready_for_review only) */}
                    {post.status === 'ready_for_review' && (
                      <>
                        <button className="btn btn-sm" style={{ background: '#10B981', color: '#fff', border: 'none' }}
                          onClick={() => handleApprove(post)} disabled={isActioning} title="Freigeben">
                          <ThumbsUp size={12} />
                        </button>
                        <button className="btn btn-sm" style={{ background: '#EF4444', color: '#fff', border: 'none' }}
                          onClick={() => setRejectModalPost(post)} disabled={isActioning} title="Ablehnen">
                          <ThumbsDown size={12} />
                        </button>
                      </>
                    )}

                    {/* Mark as posted (approved/scheduled) */}
                    {['approved', 'scheduled'].includes(post.status) && (
                      <button className="btn btn-sm" style={{ background: '#10B981', color: '#fff', border: 'none' }}
                        onClick={() => apiAction(post.id, 'mark-posted')} disabled={isActioning} title="Als gepostet markieren">
                        {isActioning ? <Loader2 size={12} className="spin" /> : <Instagram size={12} />}
                      </button>
                    )}

                    {/* Engagement (posted only) */}
                    {post.status === 'posted' && (
                      <button className="btn btn-secondary btn-sm" onClick={() => { setEngagementModalPost(post); }} title="Engagement eintragen">
                        <BarChart3 size={12} />
                      </button>
                    )}

                    {/* Copy */}
                    <button className="btn btn-secondary btn-sm" onClick={() => copyCaption(post)} title="Caption kopieren">
                      {copiedId === post.id ? <CheckCircle size={12} /> : <Copy size={12} />}
                    </button>

                    <div style={{ flex: 1 }} />

                    {/* Archive */}
                    {!['archived'].includes(post.status) && (
                      <button className="btn btn-secondary btn-sm" onClick={() => apiAction(post.id, 'archive')} title="Archivieren">
                        <Archive size={12} />
                      </button>
                    )}

                    {/* Delete */}
                    <button className="btn btn-danger btn-sm" onClick={() => setDeleteConfirmId(post.id)} title="Löschen">
                      <Trash2 size={12} />
                    </button>
                    {deleteConfirmId === post.id && (
                      <div style={{ position: 'fixed', inset: 0, zIndex: 100, display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'rgba(0,0,0,0.7)' }} onClick={() => setDeleteConfirmId('')}>
                        <div style={{ background: '#1a1a2e', borderRadius: 16, padding: 28, maxWidth: 360, width: '90%', boxShadow: '0 20px 60px rgba(0,0,0,0.5)', border: '1px solid rgba(255,255,255,0.1)' }} onClick={e => e.stopPropagation()}>
                          <h3 style={{ fontSize: 17, fontWeight: 700, marginBottom: 8, color: '#fff' }}>🗑️ Post löschen?</h3>
                          <p style={{ fontSize: 14, marginBottom: 20, color: 'rgba(255,255,255,0.7)', lineHeight: 1.5 }}>Dieser Post wird unwiderruflich gelöscht.</p>
                          <div className="flex gap-3 justify-end">
                            <button className="btn btn-ghost" onClick={() => setDeleteConfirmId('')} style={{ padding: '8px 16px', fontSize: 13 }}>Abbrechen</button>
                            <button className="btn btn-danger" onClick={() => deletePost(post.id)} style={{ padding: '8px 20px', fontSize: 13 }}>Löschen</button>
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* ─── APPROVE/SCHEDULE MODAL ──────────────────────── */}
      {scheduleModalPost && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.6)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }}
          onClick={() => setScheduleModalPost(null)}>
          <div className="card" style={{ padding: 24, width: 400, maxWidth: '90vw' }} onClick={e => e.stopPropagation()}>
            <div className="flex justify-between items-center mb-4">
              <h3 style={{ margin: 0 }}><ThumbsUp size={18} style={{ color: '#10B981' }} /> Post freigeben</h3>
              <button className="btn btn-secondary btn-sm" onClick={() => setScheduleModalPost(null)}><X size={14} /></button>
            </div>
            <p className="text-sm text-muted mb-3">"{scheduleModalPost.title || 'Ohne Titel'}"</p>

            <div style={{ marginBottom: 16, padding: 12, borderRadius: 8, background: 'rgba(139,92,246,0.06)', border: '1px solid rgba(139,92,246,0.15)' }}>
              <div className="flex items-center gap-2 mb-2">
                <Calendar size={14} style={{ color: '#8B5CF6' }} />
                <span style={{ fontSize: 13, fontWeight: 600 }}>Veröffentlichung planen (optional)</span>
              </div>
              <div className="flex gap-2">
                <input type="date" className="input" value={scheduleDate} onChange={e => setScheduleDate(e.target.value)}
                  style={{ flex: 2, fontSize: 13 }} min={new Date().toISOString().split('T')[0]} />
                <input type="time" className="input" value={scheduleTime} onChange={e => setScheduleTime(e.target.value)}
                  style={{ flex: 1, fontSize: 13 }} />
              </div>
            </div>

            <div className="flex gap-2">
              <button className="btn btn-primary" onClick={() => confirmApprove(!!scheduleDate)}>
                <Check size={14} /> {scheduleDate ? 'Planen & Freigeben' : 'Sofort freigeben'}
              </button>
              <button className="btn btn-secondary" onClick={() => setScheduleModalPost(null)}>Abbrechen</button>
            </div>
          </div>
        </div>
      )}

      {/* ─── REJECT MODAL ──────────────────────────────── */}
      {rejectModalPost && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.6)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }}
          onClick={() => setRejectModalPost(null)}>
          <div className="card" style={{ padding: 24, width: 400, maxWidth: '90vw' }} onClick={e => e.stopPropagation()}>
            <div className="flex justify-between items-center mb-4">
              <h3 style={{ margin: 0 }}><ThumbsDown size={18} style={{ color: '#EF4444' }} /> Post ablehnen</h3>
              <button className="btn btn-secondary btn-sm" onClick={() => setRejectModalPost(null)}><X size={14} /></button>
            </div>

            <div style={{ marginBottom: 12 }}>
              <label className="text-xs text-muted mb-1" style={{ display: 'block' }}>Ablehnungsgrund</label>
              <select className="input" value={rejectReason} onChange={e => setRejectReason(e.target.value)} style={{ width: '100%', fontSize: 13 }}>
                <option value="">Bitte wählen...</option>
                {REJECTION_REASONS.map(r => <option key={r.value} value={r.value}>{r.label}</option>)}
              </select>
            </div>

            <div style={{ marginBottom: 16 }}>
              <label className="text-xs text-muted mb-1" style={{ display: 'block' }}>Kommentar (optional)</label>
              <textarea className="input" value={rejectComment} onChange={e => setRejectComment(e.target.value)}
                rows={3} placeholder="Was soll geändert werden?" style={{ width: '100%', fontSize: 13, resize: 'vertical' }} />
            </div>

            <div className="flex gap-2">
              <button className="btn" style={{ background: '#EF4444', color: '#fff', border: 'none' }} onClick={handleReject} disabled={!rejectReason}>
                <ThumbsDown size={14} /> Ablehnen
              </button>
              <button className="btn btn-secondary" onClick={() => setRejectModalPost(null)}>Abbrechen</button>
            </div>
          </div>
        </div>
      )}

      {/* ─── ENGAGEMENT MODAL ──────────────────────────── */}
      {engagementModalPost && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.6)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }}
          onClick={() => setEngagementModalPost(null)}>
          <div className="card" style={{ padding: 24, width: 420, maxWidth: '90vw' }} onClick={e => e.stopPropagation()}>
            <div className="flex justify-between items-center mb-4">
              <h3 style={{ margin: 0 }}><BarChart3 size={18} style={{ color: '#3B82F6' }} /> Engagement eintragen</h3>
              <button className="btn btn-secondary btn-sm" onClick={() => setEngagementModalPost(null)}><X size={14} /></button>
            </div>
            <p className="text-xs text-muted mb-3">"{engagementModalPost.title || 'Ohne Titel'}"</p>

            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8, marginBottom: 16 }}>
              {[
                { key: 'likes', label: '❤️ Likes' },
                { key: 'comments', label: '💬 Kommentare' },
                { key: 'saves', label: '🔖 Saves' },
                { key: 'shares', label: '📤 Shares' },
                { key: 'reach', label: '👁 Reach' },
                { key: 'impressions', label: '📊 Impressions' },
              ].map(f => (
                <div key={f.key}>
                  <label className="text-xs text-muted" style={{ display: 'block', marginBottom: 2 }}>{f.label}</label>
                  <input
                    type="number" className="input" min="0"
                    value={(engagementData as any)[f.key]}
                    onChange={e => setEngagementData(d => ({ ...d, [f.key]: parseInt(e.target.value) || 0 }))}
                    style={{ width: '100%', fontSize: 13 }}
                  />
                </div>
              ))}
            </div>

            <div className="flex gap-2">
              <button className="btn btn-primary" onClick={handleSaveEngagement}>
                <Check size={14} /> Speichern
              </button>
              <button className="btn btn-secondary" onClick={() => setEngagementModalPost(null)}>Abbrechen</button>
            </div>
          </div>
        </div>
      )}

      <style>{`@keyframes spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } } .spin { animation: spin 1s linear infinite; }`}</style>
    </div>
  );
};
